
import React, { useState } from "react";
import { SpendByProductPanel } from "./SpendByProductPanel";
import { SpendByGeographyPanel } from "./SpendByGeographyPanel";
import { FishboneCategorySpendTree } from "./FishboneCategorySpendTree";
import { groupCubeRows, type ExecutiveTransaction } from "../../data/executiveOverview";

const filters = [
  { label: "UNSPSC", value: "unspsc" },
  { label: "Fishbone", value: "fishbone" },
];
const metrics = [
  { label: "Spend", value: "spend" },
  { label: "Transaction", value: "transaction" },
  { label: "Suppliers", value: "suppliers" },
];
const barColors = ["#492750", "#7c5cbf", "#3d2d6b", "#9575d0", "#6b46b0", "#4e3585", "#8162c2", "#5e448f", "#10b981", "#f59e0b", "#ef4444", "#3b82f6", "#b42318", "#b54708", "#b68900"];

export function SpendCubeCategoryChart({ rows }: { rows: ExecutiveTransaction[] }) {
  const [activeFilter, setActiveFilter] = useState("fishbone");
  const [activeMetric, setActiveMetric] = useState("spend");
  const groups = groupCubeRows(rows, activeFilter === "fishbone" ? "Category_L2" : (row) => `UNSPSC ${row.UNSPSC_Code.slice(0, 2)}`);
  const values = groups.map((group) => ({ ...group, value: activeMetric === "transaction" ? group.count : activeMetric === "suppliers" ? group.suppliers : group.amount / 1_000_000 }))
    .sort((first, second) => second.value - first.value);
  const maxValue = Math.max(...values.map((group) => group.value), 0);
  const axisMax = maxValue || 1;

  return (
    <>
      <div className="spendcube-panel">
        {/* Row 1: Header and Filters */}
        <div className="spendcube-panel__header">
          <div className="spendcube-panel__title">SPEND BY CATEGORY</div>
          <div className="spendcube-panel__filters">
            <div className="spendcube-panel__filter-group">
              {filters.map((f) => (
                <button
                  key={f.value}
                  className={`spendcube-panel__filter-btn${activeFilter === f.value ? " is-active" : ""}`}
                  onClick={() => setActiveFilter(f.value)}
                  type="button"
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="spendcube-panel__metric-group">
              {metrics.map((m) => (
                <button
                  key={m.value}
                  className={`spendcube-panel__metric-btn${activeMetric === m.value ? " is-active" : ""}`}
                  onClick={() => setActiveMetric(m.value)}
                  type="button"
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        {/* Row 2: Bar Chart */}
        <div className="spendcube-panel__chart-wrap">
          <div className="spendcube-panel__yaxis">
            {[4, 3, 2, 1, 0].map((step) => (
              <div key={step} className="spendcube-panel__yaxis-label">{activeMetric === "spend" ? `${(maxValue * step / 4).toFixed(1)} M` : Math.round(maxValue * step / 4).toLocaleString()}</div>
            ))}
          </div>
          <div className="spendcube-panel__bars-wrap">
            <div className="spendcube-panel__bars-divider" />
            <div className="spendcube-panel__bars" style={{ gridTemplateColumns: `repeat(${Math.max(values.length, 1)}, 1fr)` }}>
              {!values.length && <div style={{ gridColumn: "1 / -1", alignSelf: "center", color: "#888", textAlign: "center" }}>No spend matches the selected filters.</div>}
              {values.map((bar, index) => (
                <div key={bar.label} className="spendcube-panel__bar-group">
                  <div
                    className="spendcube-panel__bar"
                    style={{ height: `${bar.value / axisMax * 200}px`, background: barColors[index % barColors.length] }}
                    title={`${bar.label}: ${activeMetric === "spend" ? `$${bar.value.toFixed(1)}M` : bar.value.toLocaleString()}`}
                  />
                  <div className="spendcube-panel__bar-label" title={bar.label}>{bar.label}</div>
                </div>
              ))}
            </div>
            <div className="spendcube-panel__bars-bottom" />
          </div>
        </div>
      </div>
      {/* Two-column row: Spend by Product and Spend by Geography */}
      <div className="spendbyproduct-row">
        <div className="spendbyproduct-col">
          <SpendByProductPanel rows={rows} />
        </div>
        <div className="spendbyproduct-col">
          <SpendByGeographyPanel rows={rows} />
        </div>
      </div>
      {/* Full-width Fishbone Category Spend Tree */}
      <div style={{ width: '100%' }}>
        <FishboneCategorySpendTree rows={rows} />
      </div>
    </>
  );
}
